
import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch } from "react-redux";
import { baseApi } from "../../../../services/redux/api/baseApi";
// Formik/yup removed - inline update controls used instead
import { LuSave } from "react-icons/lu";
import { useFupList, useFupOperations } from "../../../../services/redux/api/components";
import ListView from "../../shared/ListView";
import { toast } from "react-toastify";
import { Search } from "lucide-react";

const columns = [
  { key: "policyNo", label: "Policy No", searchable: true },
  { key: "lifeAssured", label: "Life Assured", searchable: true },
  { key: "groupCode", label: "Group Code", searchable: true },
  { key: "product", label: "Product", searchable: true },
  { key: "agency", label: "Agency", searchable: true },
];

// validation handled inline when update button pressed

export default function BatchFupCompactScrollable() {
  const [policies, setPolicies] = useState([]);
  const [pageNumber, setPageNumber] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [searchField, setSearchField] = useState("lifeAssured");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [manualSearch, setManualSearch] = useState(null);

  const columnMap = useMemo(
    () => ({
      policyNo: "PolicyNo",
      lifeAssured: "LifeAssured",
      groupCode: "GroupCode",
      // Use 'Product' here so the SP receives the DB column it expects;
      // some SPs don't support searching on derived 'ProductName'.
      product: "Product",
      agency: "AgencyCode",
    }),
    []
  );

  // Custom debounce hook behaviour implemented inline to avoid extra files.
  // debouncedSearch follows searchQuery after the configured delay unless the user
  // explicitly applies the filter (manualSearch) by pressing Enter or clicking Filter.
  useEffect(() => {
    // When the user types a new value, clear any previous manual apply (so typing resumes debounce)
    if (manualSearch !== null && (searchQuery || '').trim() !== manualSearch) {
      setManualSearch(null);
    }

    const handler = setTimeout(() => {
      setDebouncedSearch((searchQuery || '').trim());
    }, 300);

    return () => clearTimeout(handler);
  }, [searchQuery, manualSearch]);

  const listArgs = useMemo(
    () => ({
      pageNumber: pageNumber,
      pageSize: pageSize,
      searchColumn: columnMap[searchField] ?? null,
      // If the user explicitly applied a filter, prefer that immediate value.
      searchValue: (manualSearch !== null ? manualSearch : debouncedSearch) || null,
      fromFupDate: null,
      toFupDate: null,
      sortField: null,
      sortOrder: "asc",
      fetchAll: false,
    }),
    [columnMap, searchField, debouncedSearch, manualSearch, pageNumber, pageSize]
  );

  const apiListResult = useFupList(listArgs);
  const ops = useFupOperations();
  const dispatch = useDispatch();
  const gridRef = useRef(null);
  const selectedRowsRef = useRef([]);
  const [newFupDate, setNewFupDate] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  const { apiRows, apiLoading } = useMemo(() => {
    const result = apiListResult ?? {};
    let rows = [];
    if (result?.data) {
      const payload = result.data;
      if (Array.isArray(payload.data)) rows = payload.data;
      else if (Array.isArray(payload)) rows = payload;
      else if (Array.isArray(payload.rawRecordsets) && Array.isArray(payload.rawRecordsets[0])) rows = payload.rawRecordsets[0];
    } else if (Array.isArray(result?.rawRecordsets) && Array.isArray(result.rawRecordsets[0])) rows = result.rawRecordsets[0];
    else if (Array.isArray(result?.recordsets) && Array.isArray(result.recordsets[0])) rows = result.recordsets[0];
    else if (Array.isArray(result)) rows = result;
    return { apiRows: rows, apiLoading: result.isLoading ?? false };
  }, [apiListResult]);

  const totalCount = useMemo(() => {
    const result = apiListResult ?? {};
    return Number(result?.data?.totalCount ?? result?.totalCount ?? (Array.isArray(apiRows) ? apiRows.length : 0));
  }, [apiListResult, apiRows]);

  useEffect(() => {
    setPolicies(apiRows || []);
  }, [apiRows]);

  // Prefetch next page to improve perceived pagination speed.
  useEffect(() => {
    try {
      if (!apiListResult) return;
      const loading = apiListResult.isLoading ?? false;
      if (loading) return; // wait until current finished
      const currentTotal = Number(totalCount || 0);
      if (!currentTotal || !pageSize) return;
      const maxPage = Math.max(1, Math.ceil(currentTotal / pageSize));
      const nextPage = Number(pageNumber) + 1;
      if (nextPage > maxPage) return;
      // build payload for next page (same filters)
      const prefetchPayload = { ...listArgs, pageNumber: nextPage };
      // dispatch RTK Query initiate to warm the cache
      dispatch(baseApi.endpoints.getFupPolicies.initiate(prefetchPayload, { force: false }));
    } catch (e) {
      // ignore prefetch failures
      // console.debug('prefetch failed', e);
    }
  }, [apiListResult, totalCount, pageNumber, pageSize, listArgs, dispatch]);

  const getKey = (p) => p?.PolicyID ?? p?.id ?? p?.PolicyNo ?? p?.policyNo ?? JSON.stringify(p);

  const getFirstField = (obj, keys = []) => {
    if (!obj || !keys || !keys.length) return null;
    for (const k of keys) {
      if (obj[k] !== undefined && obj[k] !== null && obj[k] !== "") return obj[k];
      if (obj.__raw && obj.__raw[k] !== undefined && obj.__raw[k] !== null && obj.__raw[k] !== "") return obj.__raw[k];
      const lower = k.charAt(0).toLowerCase() + k.slice(1);
      if (obj[lower] !== undefined && obj[lower] !== null && obj[lower] !== "") return obj[lower];
    }
    return null;
  };

  const rowData = useMemo(() => {
    const source = apiRows && apiRows.length > 0 ? apiRows : policies || [];
    if (!Array.isArray(source)) return [];
    return source.map((item, idx) => {
      const canonical = item.PolicyId ?? item.PolicyID ?? item.id ?? item.Id ?? idx;
      return { ...item, PolicyId: canonical, id: canonical };
    });
  }, [apiRows, policies]);

  // Header component factory that renders a title and a small search button.
  // Clicking the search button selects the corresponding filter column and focuses the filter input.
  const createHeaderWithSearch = (uiKey, displayName) => {
    return function Header() {
      return (
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium">{displayName}</div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSearchField(uiKey);
              // focus the shared filter input in ListView
              try {
                const el = document.getElementById('filterValue');
                if (el) el.focus();
              } catch (err) {
                // ignore
              }
            }}
            className="ml-2 text-gray-500 hover:text-brandRed px-1"
            title={`Filter by ${displayName}`}
          >
            <Search size={16} color={'green'} />
          </button>
        </div>
      );
    };
  };

  const columnDefs = useMemo(
    () => [
      { headerCheckboxSelection: true, checkboxSelection: true, width: 50, suppressMenu: true },
      {
        headerName: "Policy No",
        field: "PolicyNumber",
        minWidth: 160,
        flex: 1.6,
        headerComponent: createHeaderWithSearch('policyNo', 'Policy No'),
        tooltipField: 'PolicyNumber',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const val = d?.PolicyNumber || d?.PolicyNo || params.value || "";
          return (
            <span title={val} className="text-sm break-words">
              {val}
            </span>
          );
        },
      },
      {
        headerName: "Life Assured",
        field: "LifeAssured",
        minWidth: 220,
        flex: 2,
        headerComponent: createHeaderWithSearch('lifeAssured', 'Life Assured'),
        tooltipField: 'LifeAssured',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const val = d?.LifeAssuredName || d?.LifeAssured || d?.CustomerName || params.value || "";
          return (
            <span title={val} className="text-sm break-words">
              {val}
            </span>
          );
        },
      },
      {
        headerName: "Group Code",
        field: "GroupCode",
        minWidth: 110,
        width: 120,
        headerComponent: createHeaderWithSearch('groupCode', 'Group Code'),
        tooltipField: 'GroupCode',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const val = d?.GroupCode || d?.Group || d?.GroupID || params.value || "";
          return <span title={val} className="text-sm">{val}</span>;
        },
      },
      {
        headerName: "Product",
        field: "ProductName",
        minWidth: 160,
        flex: 1.6,
        headerComponent: createHeaderWithSearch('product', 'Product'),
        tooltipField: 'ProductName',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const val = d?.ProductName || d?.Product || params.value || "";
          return <span title={val} className="text-sm">{val}</span>;
        },
      },
      {
        headerName: "Agency",
        field: "AgencyCode",
        minWidth: 140,
        flex: 1,
        headerComponent: createHeaderWithSearch('agency', 'Agency'),
        tooltipField: 'AgencyCode',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const val = d?.AgencyName || d?.Agency || d?.AgencyCode || params.value || "";
          return <span title={val} className="text-sm">{val}</span>;
        },
      },
      {
        headerName: "FUP Date",
        field: "FupDate",
        width: 140,
        tooltipField: 'FupDate',
        cellRenderer: (params) => {
          const d = params?.data || {};
          const raw = d?.FupDate || d?.FUPDate || d?.Fup_Date || params.value || "";
          if (!raw) return <span className="text-sm"></span>;
          // Format YYYY-MM-DD -> DD/MM/YYYY when possible
          const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
          const s = match ? `${match[3]}/${match[2]}/${match[1]}` : String(raw).slice(0, 10);
          return <span title={String(raw)} className="text-sm">{s}</span>;
        },
      },
      { headerName: "Sum Assured", field: "SumAssured", width: 140 },
      { headerName: "Payment", field: "PaymentMode", width: 120 },
      { headerName: "Net", field: "NetPremium", width: 120 },
      { headerName: "Term", field: "Term", width: 80 },
      { headerName: "PPT", field: "PPT", width: 80 },
    ],
    []
  );

  const onSelectionChanged = (params) => {
    try {
      const rows = params.api.getSelectedRows();
      selectedRowsRef.current = rows;
    } catch (e) {
      selectedRowsRef.current = [];
    }
  };

  const callUpdate = async (payload) => {
    if (!ops || typeof ops.batchUpdateFup !== "function") throw new Error("batchUpdateFup mutation not available");
    const trigger = ops.batchUpdateFup;
    return trigger(payload).unwrap ? trigger(payload).unwrap() : trigger(payload);
  };

  return (
    <div className="p-4 lg:p-6">
      <div className="">
        <div className="mb-4">
          <div className="text-xl font-bold text-brandRed">Batch FUP Update</div>
        </div>

        {/* pass update controls into ListView so they render just before pagination */}
        <ListView
          filterColumn={searchField}
          setFilterColumn={setSearchField}
          columnOptions={columns.map((c) => ({ value: c.key, label: c.label }))}
          filterValue={searchQuery}
          setFilterValue={setSearchQuery}
          handleFilter={() => {
            const v = (searchQuery || '').trim();
            // apply immediately (manual) and also update debounced so UI shows applied state
            setManualSearch(v);
            setDebouncedSearch(v);
          }}
          clearFilters={() => {
            setSearchQuery("");
            setDebouncedSearch("");
            setManualSearch(null);
          }}
          isFilterApplied={!!(manualSearch || debouncedSearch)}
          gridRef={gridRef}
          columnDefs={columnDefs}
          rowData={rowData}
          itemsPerPage={pageSize}
          currentPage={pageNumber}
          setCurrentPage={setPageNumber}
          onSelectionChanged={onSelectionChanged}
          defaultColDef={{ sortable: true, resizable: true, suppressMenu: true }}
          loading={apiLoading}
          handleItemsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPageNumber(1);
          }}
          totalCount={totalCount}
          beforePagination={
            <div className="flex justify-center items-center gap-3">
              <input
                type="date"
                value={newFupDate}
                onChange={(e) => setNewFupDate(e.target.value)}
                className="pl-2 pr-2 py-2 rounded-none border border-gray-300 text-sm"
                aria-label="New FUP Date"
              />

              <button
                type="button"
                disabled={isUpdating}
                 onClick={async () => {
                  if (!newFupDate) {
                    toast.error("Please select a new FUP date before updating");
                    return;
                  }
                  const rows = selectedRowsRef.current || [];
                  if (!rows.length) {
                    toast.error("Please select at least one policy to update");
                    return;
                  }
                  setIsUpdating(true);
                  try {
                    const ids = rows.map((r) => (r.PolicyId ?? r.PolicyID ?? r.id ?? r.PolicyNo ?? r.PolicyNo));
                    const payload = { ids, newFupDate, updatedBy: null };
                    const res = await callUpdate(payload);
                    try {
                      console.debug('[FUP:BatchFupUpdate] batchUpdateFup response:', res);
                    } catch (e) {}
                    // Only treat as success when updatedCount > 0
                    if (res && Number(res.updatedCount) > 0) {
                      try {
                        const updated = Array.isArray(res.updated) ? res.updated : [];
                          if (updated.length > 0) {
                          // Merge server-returned rows into cached rows but avoid overwriting with null/undefined.
                          dispatch(
                            baseApi.util.updateQueryData(baseApi.endpoints.getFupPolicies, listArgs, (draft) => {
                              try {
                                const target = draft && draft.data ? draft.data : Array.isArray(draft) ? draft : [];
                                for (const u of updated) {
                                  const uid = u.PolicyId ?? u.PolicyID ?? u.PolicyNo ?? u.PolicyNumber ?? u.PolicyId;
                                  for (const rec of target) {
                                    const rid = rec?.PolicyId ?? rec?.PolicyID ?? rec?.id ?? rec?.PolicyNo ?? rec?.PolicyNumber;
                                    if (rid != null && String(rid) === String(uid)) {
                                      // Merge properties but only assign when the incoming value is not null/undefined
                                      for (const k of Object.keys(u)) {
                                        const v = u[k];
                                        if (v !== null && v !== undefined) {
                                          // preserve existing values when server returns null/undefined
                                          rec[k] = v;
                                        }
                                      }
                                    }
                                  }
                                }
                              } catch (e) {
                                // ignore
                              }
                            })
                          );
                        } else {
                          // Fallback: mark selected rows' FUP date in cache
                          const ids = rows.map((r) => (r.PolicyId ?? r.PolicyID ?? r.id ?? r.PolicyNo ?? r.PolicyNo));
                          dispatch(
                            baseApi.util.updateQueryData(baseApi.endpoints.getFupPolicies, listArgs, (draft) => {
                              try {
                                const target = draft && draft.data ? draft.data : Array.isArray(draft) ? draft : [];
                                for (const id of ids) {
                                  for (const rec of target) {
                                    const rid = rec?.PolicyId ?? rec?.PolicyID ?? rec?.id ?? rec?.PolicyNo ?? rec?.PolicyNumber;
                                    if (rid != null && String(rid) === String(id)) {
                                      rec.FupDate = newFupDate;
                                      rec.FUPDate = newFupDate;
                                      rec.Fup_Date = newFupDate;
                                    }
                                  }
                                }
                              } catch (e) {}
                            })
                          );
                        }
                      } catch (e) {
                        // ignore cache update errors
                      }

                      toast.success("FUP dates updated successfully");
                      setNewFupDate("");
                    } else {
                      toast.error("No policies were updated. Please check and try again.");
                    }
                  } catch (err) {
                    console.error(err);
                    toast.error(err?.message || "Failed to update FUP dates");
                  } finally {
                    setIsUpdating(false);
                  }
                }}
                className="
    inline-flex items-center gap-2
    px-5 py-2 
    font-semibold 
    
    border
    border-green-600      /* FIX: Set dark green border */
    text-green-700
    bg-green-100
    hover:bg-green-200
    transition
    disabled:opacity-60
  "
              >
                <LuSave size={14} className="text-green-700" />
                <span>{isUpdating ? 'Updating...' : 'Update'}</span>
              </button>


            </div>
          }
        />

        {/* bottom duplicate Update controls removed; keep single control rendered via ListView.beforePagination */}
      </div>
    </div>
  );
}
